import { Resource, WishlistService, injectable, WishlistCreate } from '@rdss/client'
import { store } from '~/store';
import { keyValueStore } from '@rdss/core';
import { Build } from '@stencil/core';

/**
 * @class WeinfreundeWishlistService
 * @augments {WishlistService}
 *
 * The Weinfreunde WishlistService handles the operations for the cart note api resource.
 * It extends the WishlistService from the '@rdss/client' package.
 * It provides methods to add, remove and get notes from the wishlist.
 */
@injectable()
@Resource('wishlist')
// eslint-disable-next-line require-jsdoc
export class WeinfreundeWishlistService extends WishlistService {
    private storeName = 'wishlist'
    private clearStorageOnRoutes = ['/note']

    /**
     * This method adds a note to the wishlist.
     * It first creates the item using the create method from the WishlistService,
     * then it adds the item to the wishlist in the store.
     * After that, it stores the wishlist and dispatches an update event.
     *
     * @param {WishlistCreate} item - The item to be added to the wishlist.
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    async addNote(item: WishlistCreate): Promise<void> {
        await this.create(item)

        store.state.wishlist = [...store.state.wishlist, item]

        await this.storeWishlist()

        this.dispatchUpdateEvent('wishlistItemAdded')
    }

    /**
     * This method removes a note from the wishlist.
     * It first deletes the item using the delete method from the WishlistService,
     * then it removes the item from the wishlist in the store.
     * After that, it stores the wishlist and dispatches an update event.
     *
     * @param {number} id - The id of the item to be removed from the wishlist.
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    async removeNote(id: number): Promise<void> {
        await this.delete(`delete/${id}`)

        store.state.wishlist = store.state.wishlist.filter(item => item.id !== id)

        await this.storeWishlist()

        this.dispatchUpdateEvent('wishlistItemRemoved')
    }

    /**
     * Retrieves the notes from the wishlist.
     *
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    async getNotes(): Promise<void> {
        await this.clearWishlistStorageIfOnClearRoute()

        const storedWishlist = await keyValueStore.get(this.storeName)

        try {
            store.state.wishlist = storedWishlist ?? await this.find().then(res => res.data)
        } catch (e) {
            console.debug('Error while getting wishlist', e.message)
        }

        await this.storeWishlist()
    }

    /**
     * Clears the wishlist storage.
     *
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    async clearWishlist(): Promise<void>{
        await keyValueStore.set(this.storeName, null)
    }

    /**
     * This private method stores the wishlist using the keyValueStore.
     *
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    private async storeWishlist(): Promise<void> {
        if (Build.isBrowser) await keyValueStore.set(this.storeName, store.state.wishlist)
    }

    /**
     * This private method dispatches an update event on the document element.
     *
     * @param {string} eventType - The type of the event to dispatch.
     */
    private dispatchUpdateEvent = (eventType: string) =>
        document.documentElement.dispatchEvent(new CustomEvent(eventType, { bubbles: true }))

    /**
     * Clears the wishlist storage on specific routes.
     *
     * If the current path is included in the `clearOnRoutes` array and the environment is a browser,
     * the wishlist in the keyValueStore will be cleared.
     *
     * @returns {Promise<void>} A Promise that resolves when the operation is complete.
     */
    private async clearWishlistStorageIfOnClearRoute(): Promise<void>{
        const shouldResetWishlist = Build.isBrowser && this.clearStorageOnRoutes.includes(location.pathname)

        if (shouldResetWishlist) {
            await this.clearWishlist()
        }
    }
}
